import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';
import FaqSchema from '../components/FaqSchema';
import { 
  ChevronDown, 
  ChevronUp, 
  Check, 
  ArrowRight, 
  Brain, 
  ScanEye, 
  MousePointerClick, 
  CalendarCheck, 
  HeartPulse, 
  Waves, 
  Route, 
  Info, 
  FileSearch, 
  SearchCheck, 
  FileCheck, 
  Pill, 
  RefreshCw 
} from 'lucide-react';

export default function ADHD() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsPage, setCmsPage] = useState(null);
  const [cmsFaqs, setCmsFaqs] = useState([]);
  const [cmsArticles, setCmsArticles] = useState([]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch('/_api/public/content/adhd_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching adhd_side:', err);
      }
    };

    const fetchFaqs = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:adhd&limit=7&sort=sortering&order=asc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsFaqs(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching ADHD FAQs:', err);
      }
    };

    const fetchArticles = async () => {
      try {
        const res = await fetch('/_api/public/v2/content/artikler?term=kategori:adhd&limit=6&sort=updatedAt&order=desc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsArticles(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching ADHD articles:', err);
      }
    };

    fetchPage();
    fetchFaqs();
    fetchArticles();
  }, [lang]);

  const seoTitle = lang === 'da' 
    ? 'ADHD-udredning og rådgivning | Nordisk Psykiatri' 
    : 'ADHD-utredning och rådgivning | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Specialiseret udredning og rådgivning for ADHD hos unge og voksne. Få klarhed over dine symptomer og konkrete redskaber til en bedre hverdag.'
    : 'Specialiserad utredning och rådgivning för ADHD hos unga och vuxna. Få klarhet i dina symtom och konkreta verktyg för en bättre vardag.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqItems = [
    {
      q: 'Hvor lang tid tager en ADHD-udredning?',
      a: 'Et typisk udredningsforløb hos os strækker sig over 3-4 uger fra første konsultation til den afsluttende tilbagemelding og epikrise, alt efter hvor hurtigt aftalerne planlægges.'
    },
    {
      q: 'Skal jeg have en henvisning?',
      a: 'Nej, du behøver ingen henvisning fra din egen læge for at starte et privat udredningsforløb hos os. Du kan kontakte os eller booke en indledende samtale direkte.'
    },
    {
      q: 'Hvem kan udredes for ADHD?',
      a: 'Vi udreder voksne og unge fra 18 år (samt ældre unge efter konkret aftale), der oplever symptomer på uopmærksomhed, indre uro, impulsivitet eller udfordringer i hverdagen.'
    },
    {
      q: 'Dækker sygesikringen ADHD-udredning?',
      a: 'Offentlig sygesikring dækker ikke private udredninger direkte. Nogle private sundhedsforsikringer eller Sygeforsikringen "danmark" dækker dog helt eller delvist, afhængigt af din police.'
    },
    {
      q: 'Hvad hvis jeg ikke får diagnosen?',
      a: 'Formålet med udredningen er at afdække dine vanskeligheder grundigt. Hvis kriterierne for ADHD ikke er opfyldt, hjælper vi dig med at forstå, hvad dine udfordringer i stedet kan skyldes, og giver anbefalinger til videre forløb.'
    },
    {
      q: 'Hvordan forbereder jeg mig?',
      a: 'Det kan være en god idé at skrive dine vigtigste udfordringer og eksempler fra hverdagen ned samt tænke over, hvordan det var i skoletiden. Eventuelle skoleudtalelser kan også være nyttige.'
    },
    {
      q: 'Kan man få medicin efter udredningen?',
      a: 'Ja, hvis udredningen konkluderer, at du har ADHD, drøfter vi muligheden for medicinsk behandling, og du tilbydes opstart og udtrapning/justering i samråd med vores psykiater.'
    }
  ];

  const signs = [
    {
      id: 'attention',
      title: lang === 'da' ? 'Opmærksomhed' : 'Attention',
      desc: lang === 'da'
        ? 'Vanskeligheder med at fastholde fokus og bevare overblikket.'
        : 'Difficulties maintaining focus and sustaining an overview.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <circle cx="32" cy="32" r="8" stroke="currentColor" strokeWidth="1.8" />
          <path d="M21 14H15V20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M43 14H49V20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M21 50H15V44" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M43 50H49V44" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="32" cy="32" r="16" stroke="currentColor" strokeWidth="1.4" strokeDasharray="3 5" />
        </svg>
      )
    },
    {
      id: 'impulsivity',
      title: lang === 'da' ? 'Impulsivitet' : 'Impulsivity',
      desc: lang === 'da'
        ? 'En tendens til at handle eller reagere hurtigt.'
        : 'A tendency to act or react quickly.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <circle cx="32" cy="32" r="4" stroke="currentColor" strokeWidth="1.8" />
          <path d="M32 10V20" stroke="currentColor" strokeWidth="1.8" />
          <path d="M32 44V54" stroke="currentColor" strokeWidth="1.8" />
          <path d="M10 32H20" stroke="currentColor" strokeWidth="1.8" />
          <path d="M44 32H54" stroke="currentColor" strokeWidth="1.8" />
          <path d="M16 16L23 23" stroke="currentColor" strokeWidth="1.8" />
          <path d="M41 41L48 48" stroke="currentColor" strokeWidth="1.8" />
          <path d="M48 16L41 23" stroke="currentColor" strokeWidth="1.8" />
          <path d="M23 41L16 48" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      )
    },
    {
      id: 'planning',
      title: lang === 'da' ? 'Planlægning og struktur' : 'Planning and structure',
      desc: lang === 'da'
        ? 'Udfordringer med at organisere og prioritere opgaver.'
        : 'Challenges organizing and prioritizing tasks.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <rect x="17" y="28" width="20" height="20" stroke="currentColor" strokeWidth="1.8" />
          <path d="M27 16H48V37" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      )
    },
    {
      id: 'emotional',
      title: lang === 'da' ? 'Følelsesregulering' : 'Emotional regulation',
      desc: lang === 'da'
        ? 'Stærke følelsesmæssige reaktioner og lav stresstærskel.'
        : 'Strong emotional reactions and low stress tolerance.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <path d="M32 49L17 34C11 28 13 18 21 16C26 15 30 18 32 22C34 18 38 15 43 16C51 18 53 28 47 34L32 49Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      id: 'restlessness',
      title: lang === 'da' ? 'Indre uro' : 'Inre oro',
      desc: lang === 'da'
        ? 'Tankemylder, rastløshed eller et højt indre tempo.'
        : 'Tankemyller, rastlöshet eller ett högt inre tempo.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 25C16 18 20 18 24 25C28 32 32 32 36 25C40 18 44 18 48 25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 34C16 27 20 27 24 34C28 41 32 41 36 34C40 27 44 27 48 34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 43C16 36 20 36 24 43C28 50 32 50 36 43C40 36 44 36 48 43" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )
    },
    {
      id: 'everyday',
      title: lang === 'da' ? 'Hverdagen' : 'Vardagen',
      desc: lang === 'da'
        ? 'Kan påvirke arbejde, uddannelse og relationer.'
        : 'Kan påverka arbete, utbildning och relationer.',
      icon: (
        <svg viewBox="0 0 64 64" fill="none" className="w-10 h-10 text-[#315cff]" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="7" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="42" cy="22" r="6" stroke="currentColor" strokeWidth="1.8" />
          <path d="M13 49V42C13 35 18 31 24 31C30 31 35 35 35 42V49" stroke="currentColor" strokeWidth="1.8" />
          <path d="M35 49V40C35 34 39 30 44 30C49 30 53 34 53 40V49" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      )
    }
  ];

  const defaultConsiderPointsDa = [
    'Du glemmer ofte aftaler, mister ting eller har svært ved at organisere din hverdag.',
    'Du har svært ved at bevare opmærksomheden i længere tid, især ved opgaver du finder uinteressante.',
    'Du føler dig rastløs, har svært ved at sidde stille eller afbryder andre ofte.',
    'Relationer er udfordrende på grund af misforståelser eller manglende evne til at lytte.',
    'Du har oplevet at have svært ved at opnå dit fulde potentiale på arbejdet eller i studiet.',
    'Disse vanskeligheder har været til stede siden barndommen og påvirker dig fortsat negativt.',
    'Du har problemer med impulsivitet, tager forhastede beslutninger eller har svært ved at kontrollere dine følelser.'
  ];

  const defaultConsiderPointsEn = [
    'You frequently forget appointments, misplace items, or struggle to organize your daily routines.',
    'You find it difficult to sustain attention over longer periods, especially with tasks you find unengaging.',
    'You feel restless, struggle to sit still, or frequently interrupt others.',
    'Relationships are strained due to misunderstandings or difficulty listening actively.',
    'You have felt held back from reaching your full potential in your career or studies.',
    'These challenges have been present since childhood and continue to affect your daily life.',
    'You struggle with impulsivity, make hasty decisions, or find it hard to regulate your emotions.'
  ];

  const parseListItems = (rawHtml, defaultItems) => {
    if (!rawHtml || typeof rawHtml !== 'string') return defaultItems;
    const liMatches = [...rawHtml.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
      .map(m => m[1].replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);
    if (liMatches.length > 0) return liMatches;
    const pMatches = [...rawHtml.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)]
      .map(m => m[1].replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);
    if (pMatches.length > 0) return pMatches;
    const lines = rawHtml.split('\n').map(l => l.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
    return lines.length > 0 ? lines : defaultItems;
  };

  const considerPoints = cmsPage?.overveje_punkter
    ? parseListItems(getLocalizedValue(cmsPage.overveje_punkter), lang === 'da' ? defaultConsiderPointsDa : defaultConsiderPointsEn)
    : (lang === 'da' ? defaultConsiderPointsDa : defaultConsiderPointsEn);

  const stepIcons = [
    // Step 1: Speech Bubbles
    <svg key="s1" className="w-6 h-6 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a.75.75 0 0 1-.974-.94 5.942 5.942 0 0 0 .917-2.613A8.75 8.75 0 0 1 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>,
    // Step 2: List / Checklist
    <svg key="s2" className="w-6 h-6 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>,
    // Step 3: Document / File
    <svg key="s3" className="w-6 h-6 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>,
    // Step 4: Capsule / Pill
    <Pill key="s4" className="w-6 h-6 stroke-[1.5]" aria-hidden="true" />,
    // Step 5: Cycle / Followup
    <svg key="s5" className="w-6 h-6 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  ];

  const defaultSteps = [
    {
      title: lang === 'da' ? 'Klinisk interview og screening' : 'Clinical interview and screening',
      desc: lang === 'da'
        ? 'Ca. 2 timer. Samtale om symptomer, funktionsniveau og livshistorie. Inkluderer standardiserede screeningsværktøjer.'
        : 'Approx. 2 hours. Dialogue regarding symptoms, everyday functioning, and life history. Includes standardized screening tools.'
    },
    {
      title: lang === 'da' ? 'Diagnostisk udredning' : 'Diagnostic assessment',
      desc: lang === 'da'
        ? 'Ca. 2 timer. Struktureret diagnostisk interview og nærmere afdækning af relevante symptomer og funktionsområder.'
        : 'Approx. 2 hours. Structured diagnostic interview and comprehensive evaluation of relevant symptoms and functional domains.'
    },
    {
      title: lang === 'da' ? 'Tilbagemelding og behandlingsplan' : 'Feedback and treatment plan',
      desc: lang === 'da'
        ? 'Ca. 1 time. Gennemgang af resultater, skriftlig epikrise og individuel behandlingsplan.'
        : 'Approx. 1 hour. In-depth review of results, written clinical summary (epicrisis), and an individualized treatment plan.'
    },
    {
      title: lang === 'da' ? 'Medicinsk behandling' : 'Medical treatment',
      desc: lang === 'da'
        ? 'Ca. 1 time samt opfølgninger. Første konsultation hos psykiater og efterfølgende justering efter behov.'
        : 'Approx. 1 hour plus follow-ups. Initial consultation with a psychiatrist and subsequent adjustments as needed.'
    },
    {
      title: lang === 'da' ? 'Terapi og opfølgning' : 'Therapy and follow-up',
      desc: lang === 'da'
        ? 'Løbende. Psykoterapi eller psykoedukation tilpasset den enkelte.'
        : 'Ongoing. Psychotherapy or psychoeducation tailored to the individual.'
    }
  ];

  const parsedStepsFromCms = useMemo(() => {
    if (!cmsPage?.proces_trin) return null;
    const html = getLocalizedValue(cmsPage.proces_trin);
    if (!html) return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const lis = Array.from(doc.querySelectorAll('li'));
      if (lis.length > 0) {
        return lis.map((li) => {
          const strong = li.querySelector('strong, b');
          let title = '';
          let desc = '';
          if (strong) {
            title = strong.textContent.trim().replace(/^0?\d+\.\s*/, '').replace(/:$/, '').trim();
            const clone = li.cloneNode(true);
            const sClone = clone.querySelector('strong, b');
            if (sClone) sClone.remove();
            desc = clone.textContent.trim().replace(/^:\s*/, '').trim();
          } else {
            const text = li.textContent.trim();
            const colonIdx = text.indexOf(':');
            if (colonIdx > 0 && colonIdx < 60) {
              title = text.slice(0, colonIdx).replace(/^0?\d+\.\s*/, '').trim();
              desc = text.slice(colonIdx + 1).trim();
            } else {
              title = text;
              desc = '';
            }
          }
          return { title, desc };
        });
      }
    } catch (e) {
      console.error('Error parsing proces_trin', e);
    }
    return null;
  }, [cmsPage?.proces_trin, lang, getLocalizedValue]);

  const activeSteps = (parsedStepsFromCms && parsedStepsFromCms.length > 0) ? parsedStepsFromCms : defaultSteps;

  const heroEyebrow = cmsPage ? getLocalizedValue(cmsPage.hero_badge) || (lang === 'da' ? 'ADHD-UDREDNING' : 'ADHD-UTREDNING') : (lang === 'da' ? 'ADHD-UDREDNING' : 'ADHD-UTREDNING');
  const heroTitle = cmsPage ? getLocalizedValue(cmsPage.hero_overskrift) || (lang === 'da' ? 'ADHD-udredning for unge og voksne' : 'ADHD-utredning och rådgivning') : (lang === 'da' ? 'ADHD-udredning for unge og voksne' : 'ADHD-utredning och rådgivning');
  const heroDesc = cmsPage ? getLocalizedValue(cmsPage.hero_beskrivelse) || (lang === 'da' 
    ? 'Oplever du koncentrationsbesvær, indre uro, impulsivitet eller vanskeligheder med struktur og overblik? Hos Nordisk Psykiatri tilbyder vi grundig ADHD-udredning af unge og voksne samt rådgivning og behandling med udgangspunkt i din hverdag og dine behov.' 
    : 'ADHD är inte ett hinder, utan ett annat sätt att fungera på. Vi hjälper dig att hitta struktur och lugn i en hektisk vardag.') : (lang === 'da' 
    ? 'Oplever du koncentrationsbesvær, indre uro, impulsivitet eller vanskeligheder med struktur og overblik? Hos Nordisk Psykiatri tilbyder vi grundig ADHD-udredning af unge og voksne samt rådgivning og behandling med udgangspunkt i din hverdag og dine behov.' 
    : 'ADHD är inte ett hinder, utan ett annat sätt att fungera på. Vi hjälper dig att hitta struktur och lugn i en hektisk vardag.');
  const DEFAULT_HERO_IMAGE = "https://assets.auxodev.dk/cdn-cgi/image/width=2400,quality=95,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/adhd-udredning-unge-voksne-koebenhavn.jpg";
  const DEFAULT_MOBILE_HERO_IMAGE = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-29-am-1.png";

  const heroImage = (cmsPage && cmsPage.hero_billede && cmsPage.hero_billede[0])
    ? (cmsPage.hero_billede[0].includes('/cdn-cgi/image/') ? cmsPage.hero_billede[0] : `https://assets.auxodev.dk/cdn-cgi/image/width=2400,quality=95,format=auto/${cmsPage.hero_billede[0].replace('https://assets.auxodev.dk/', '')}`)
    : DEFAULT_HERO_IMAGE;
  const mobileHeroImage = (cmsPage && cmsPage.hero_billede_mobil && cmsPage.hero_billede_mobil[0])
    ? (cmsPage.hero_billede_mobil[0].includes('/cdn-cgi/image/') ? cmsPage.hero_billede_mobil[0] : `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${cmsPage.hero_billede_mobil[0].replace('https://assets.auxodev.dk/', '')}`)
    : DEFAULT_MOBILE_HERO_IMAGE;
  const heroCtaText = cmsPage ? getLocalizedValue(cmsPage.hero_cta_tekst) || (lang === 'da' ? 'Book en indledende samtale' : 'Kontakta oss om bokning') : (lang === 'da' ? 'Book en indledende samtale' : 'Kontakta oss om bokning');
  const heroCtaLink = (cmsPage && cmsPage.hero_cta_link) || "/kontakt";

  const ctaTitle = cmsPage ? getLocalizedValue(cmsPage.cta_overskrift) || (lang === 'da' ? 'Klar til at tage det næste skridt?' : 'Redo att ta nästa steg?') : (lang === 'da' ? 'Klar til at tage det næste skridt?' : 'Redo att ta næsta steg?');
  const ctaDesc = cmsPage ? getLocalizedValue(cmsPage.cta_beskrivelse) || (lang === 'da' ? 'Book en uforpligtende indledende samtale, så vi sammen kan afklare, om en ADHD-udredning er det rette for dig.' : 'Boka ett inledande samtal så att vi tillsammans kan klargöra dina behov och planera rätt förlopp.') : (lang === 'da' ? 'Book en uforpligtende indledende samtale, så vi sammen kan afklare, om en ADHD-udredning er det rette for dig.' : 'Boka ett inledande samtal så att vi tillsammans kan klargöra dina behov och planera rätt förlopp.');
  const ctaBtn = cmsPage ? getLocalizedValue(cmsPage.cta_knap_tekst) || (lang === 'da' ? 'Book en indledende samtale' : 'Boka ett inledande samtal') : (lang === 'da' ? 'Book en indledende samtale' : 'Boka ett inledande samtal');

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* C.1 HERO */}
      <FluidSubpageHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        description={heroDesc}
        ctaText={heroCtaText}
        ctaLink={heroCtaLink}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        tertiaryCtaText={lang === 'da' ? 'ADHD-test' : 'ADHD test'}
        tertiaryCtaLink="/vidensbank/adhd-screening"
        bgImageUrl={heroImage}
        bgImageAlt={lang === 'da' ? 'ADHD-udredning og rådgivning hos Nordisk Psykiatri' : 'ADHD-utredning och rådgivning hos Nordisk Psykiatri'}
        bgImageClass="w-full h-full object-cover object-right mix-blend-multiply"
        showBgOverlay={false}
        mobileShowBgBelow={true}
        mobileBgImageUrl={mobileHeroImage}
        mobileBgImageAlt={lang === 'da' ? 'ADHD-udredning hos Nordisk Psykiatri' : 'ADHD-utredning hos Nordisk Psykiatri'}
        textContainerClass="max-w-xl lg:max-w-[540px]"
        className="bg-[#F5F0E3]"
        style={{ backgroundColor: '#F5F0E3' }}
        reviewStamp={cmsPage?.fagligt_gennemgaaet ? getLocalizedValue(cmsPage.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Martin Bergholtz, læge i psykiatri | Opdateret september 2026' : 'Clinically reviewed by Martin Bergholtz, Medical Doctor in Psychiatry | Updated September 2026')}
      />

      {/* C.2 FØRSTE SAMLEDE SEKTION (Hvad er ADHD? + Hvordan viser det sig?) */}
      <section className="py-12 lg:py-20 bg-sand-light">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 space-y-8">
          
          {/* Main Info Card */}
          <div 
            className="relative rounded-[22px] border border-[#30463c]/18 overflow-hidden shadow-xs"
            style={{
              background: 'radial-gradient(circle at 40% 15%, rgba(247, 241, 232, 0.8), transparent 28%), #fbf9f5'
            }}
          >
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-[#30463c]/15 min-h-[700px]">
              
              {/* Left Column (~33% width) - Hvad er ADHD? */}
              <div className="lg:col-span-4 p-8 sm:p-12 lg:p-14 relative flex flex-col justify-between">
                <div>
                  <span className="block text-xs sm:text-sm font-bold tracking-[0.16em] uppercase text-[#143f37] mb-5 sm:mb-6">
                    {cmsPage ? getLocalizedValue(cmsPage.hvad_er_badge) || (lang === 'da' ? 'BAGGRUND' : 'BACKGROUND') : (lang === 'da' ? 'BAGGRUND' : 'BACKGROUND')}
                  </span>
                  <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal text-[#143f37] leading-[1.03] mb-6">
                    {lang === 'da' ? <>Hvad er<br />ADHD?</> : <>What is<br />ADHD?</>}
                  </h2>
                  <div className="w-11 h-[2px] bg-[#143f37] my-6 sm:my-8" />
                  
                  {cmsPage?.hvad_er_tekst ? (
                    <div className="text-sm sm:text-[15px] text-[#374844] leading-[1.65] max-w-[320px] [&>p]:mb-4 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsPage.hvad_er_tekst) }} />
                  ) : (
                    <p className="text-sm sm:text-[15px] text-[#374844] leading-[1.65] max-w-[320px]">
                      {lang === 'da' 
                        ? 'ADHD er en neuroudviklingsforstyrrelse, som kan påvirke opmærksomhed, impulskontrol og aktivitetsniveau.'
                        : 'ADHD is a neurodevelopmental disorder that can affect attention, impulse control, and activity levels.'}
                    </p>
                  )}
                </div>

                {/* Dekorative cirkler venstre */}
                <div className="relative mt-10 sm:mt-14 w-[240px] h-[190px] pointer-events-none self-start">
                  <div className="absolute w-[105px] h-[105px] rounded-full top-[65px] left-[10px] bg-[#6c7fc8]/25" />
                  <div className="absolute w-[105px] h-[105px] rounded-full top-0 left-[55px] bg-[#98a476]/45" />
                  <div className="absolute w-[105px] h-[105px] rounded-full top-[65px] left-[105px] bg-[#dac390]/30" />
                </div>
              </div>

              {/* Right Column (~67% width) - Hvordan kan ADHD vise sig? */}
              <div className="lg:col-span-8 p-6 sm:p-10 lg:p-12 xl:p-14 flex flex-col justify-center">
                
                {/* Heading med divider */}
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="font-display text-2xl sm:text-4xl lg:text-[42px] font-normal text-[#143f37] leading-[1.1]">
                    {cmsPage ? getLocalizedValue(cmsPage.symptomer_overskrift) || (lang === 'da' ? 'Hvordan viser ADHD sig hos unge og voksne?' : 'How does ADHD present in adolescents and adults?') : (lang === 'da' ? 'Hvordan viser ADHD sig hos unge og voksne?' : 'How does ADHD present in adolescents and adults?')}
                  </h2>

                  <div className="grid grid-cols-[1fr_28px_1fr] items-center gap-2.5 max-w-sm mx-auto my-4 sm:my-5 text-[#90ad85]">
                    <span className="h-[1px] bg-[#90ad85]" />
                    <svg viewBox="0 0 24 12" className="w-[22px] h-[12px] mx-auto text-[#90ad85]" aria-hidden="true">
                      <path
                        d="M3 9L12 2L21 9"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                      />
                    </svg>
                    <span className="h-[1px] bg-[#90ad85]" />
                  </div>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 sm:gap-x-14 lg:gap-x-16 gap-y-8 sm:gap-y-12">
                  {signs.map((item) => (
                    <article key={item.id} className="grid grid-cols-[72px_1fr] sm:grid-cols-[78px_1fr] gap-4 sm:gap-5 items-start">
                      <div className="w-[72px] sm:w-[78px] h-[72px] sm:h-[78px] rounded-full flex items-center justify-center bg-[#f5f3ef] shrink-0 shadow-2xs">
                        {item.icon}
                      </div>

                      <div className="pt-0.5">
                        <h3 className="font-display text-xl sm:text-[22px] font-normal text-[#143f37] leading-tight mb-1.5">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-[#3d4643] leading-[1.55] max-w-[240px]">
                          {item.desc}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>

              </div>

            </div>
          </div>

          {/* Screening CTA Box */}
          <div className="bg-[#1e3a35] border border-[#1e3a35] rounded-2xl p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-white mb-2">
                {cmsPage ? getLocalizedValue(cmsPage.screening_cta_overskrift) || (lang === 'da' ? 'Er du i tvivl? Tag en gratis screening' : 'In doubt? Take a free screening') : (lang === 'da' ? 'Er du i tvivl? Tag en gratis screening' : 'In doubt? Take a free screening')}
              </h3>
              <p className="text-xs sm:text-sm text-white/80 leading-relaxed max-w-2xl">
                {cmsPage ? getLocalizedValue(cmsPage.screening_cta_tekst) || (lang === 'da' ? 'Tag den officielle WHO ASRS-v1.1-selvtest for voksen-ADHD. Testen er anonym og tager kun få minutter.' : 'Take the official WHO ASRS-v1.1 self-test for adult ADHD. The test is anonymous and takes only a few minutes.') : (lang === 'da' ? 'Tag den officielle WHO ASRS-v1.1-selvtest for voksen-ADHD. Testen er anonym og tager kun få minutter.' : 'Take the official WHO ASRS-v1.1 self-test for adult ADHD. The test is anonymous and takes only a few minutes.')}
              </p>
            </div>
            <Link
              to="/vidensbank/adhd-screening"
              className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-white text-petrol-dark font-semibold text-xs sm:text-sm rounded-full shadow-xs hover:bg-sand-light transition-colors shrink-0"
            >
              <span>{lang === 'da' ? 'Start ADHD-test' : 'Start ADHD test'}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* C.3 ADD-SEKTION (Cohesive 3-part layout) */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light/40 border-y border-sand-medium/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left: Large vertical mood image */}
            <div className="lg:col-span-4 rounded-2xl overflow-hidden border border-sand-medium/60 shadow-xs min-h-[320px]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/shutterstock-2679223371.jpg"
                alt="Roligt og fokuseret rum"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Middle: Content */}
            <div className="lg:col-span-5 flex flex-col justify-center bg-sand-light rounded-2xl p-8 border border-sand-medium/60 shadow-xs">
              <span className="text-xs font-semibold tracking-wider uppercase text-petrol-medium mb-2">
                {lang === 'da' ? 'SÆRLIG PRÆSENTATIONSFORM' : 'SPECIAL PRESENTATION TYPE'}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-medium text-petrol-dark mb-4">
                {cmsPage ? getLocalizedValue(cmsPage.add_overskrift) || (lang === 'da' ? 'Hvad er ADD?' : 'What is ADD?') : (lang === 'da' ? 'Hvad er ADD?' : 'What is ADD?')}
              </h2>
              {cmsPage?.add_beskrivelse || cmsPage?.add_tekst ? (
                <div className="text-sm text-text-muted leading-relaxed mb-4 [&>p]:mb-3 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsPage.add_beskrivelse || cmsPage.add_tekst) }} />
              ) : (
                <p className="text-sm text-text-muted leading-relaxed mb-4">
                  {lang === 'da'
                    ? 'ADD er en præsentationsform af ADHD, hvor symptomer på uopmærksomhed dominerer, mens hyperaktivitet og impulsivitet er mindre fremtrædende eller helt fraværende.'
                    : 'ADD is a presentation of ADHD where symptoms of inattention dominate, while hyperactivity and impulsivity are less prominent or entirely absent.'}
                </p>
              )}
              <p className="text-sm font-semibold text-petrol-dark mb-3">
                {lang === 'da' ? 'Personer med ADD beskriver ofte:' : 'People with ADD often describe:'}
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-text-muted">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Vanskeligheder med at fastholde opmærksomheden' : 'Difficulties sustaining attention'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Tendens til dagdrømmeri eller at ‘forsvinde i egne tanker’' : 'Tendency to daydream or get lost in thought'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Langsom igangsætning af opgaver' : 'Difficulty getting started on tasks'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Mental træthed eller oplevelsen af hurtigt at blive udmattet' : 'Mental fatigue or feeling easily exhausted'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Glemsomhed og vanskeligheder med planlægning' : 'Forgetfulness and difficulty with planning'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-petrol-medium mt-1.5 shrink-0" />
                  <span>{lang === 'da' ? 'Vanskeligheder med at komme i gang eller færdiggøre opgaver' : 'Challenges initiating or completing tasks'}</span>
                </li>
              </ul>
            </div>

            {/* Right: Integrated "Relaterede udredninger" card */}
            <div className="lg:col-span-3 flex flex-col items-start text-left">
              <div className="w-full max-w-md h-full flex flex-col justify-between rounded-2xl sm:rounded-3xl border border-[#e5ebe4] bg-[#fcfdfb] p-6 sm:p-7 shadow-xs">
                <div>
                  {/* Icon Badge */}
                  <div className="mb-4 sm:mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#dfe6dd] text-[#0f3834]">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                      <circle cx="12" cy="12" r="10" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
                    </svg>
                  </div>

                  {/* Content Section */}
                  <div className="space-y-3">
                    <h3 className="font-display text-2xl font-normal text-[#0f3834] leading-tight">
                      {lang === 'da' ? 'Relaterede udredninger' : 'Related assessments'}
                    </h3>
                    <p className="font-sans text-sm leading-relaxed text-[#586c69]">
                      {lang === 'da'
                        ? 'ADHD kan optræde sammen med andre psykiatriske tilstande, og særligt ADHD og autisme ses ofte samtidig. Symptomerne kan overlappe og påvirke hinanden, og derfor er det vigtigt at se på det samlede billede. Hos Nordisk Psykiatri tilbyder vi også udredning for autisme, angst og OCD.'
                        : 'ADHD can co-occur with other psychiatric conditions, and ADHD and autism in particular are frequently seen together. Symptoms can overlap and influence each other, which is why taking a holistic view is essential. At Nordisk Psykiatri, we also offer assessments for autism, anxiety, and OCD.'}
                    </p>
                  </div>
                </div>

                {/* Actions / Tags */}
                <div className="mt-6 grid grid-cols-3 gap-2">
                  <Link
                    to="/autisme"
                    className="inline-flex items-center justify-center rounded-full border border-[#587b77] bg-white/50 py-2 px-3 text-xs sm:text-sm font-medium text-[#0f3834] transition hover:bg-[#dfe6dd]/50 text-center"
                  >
                    {lang === 'da' ? 'Autisme' : 'Autism'}
                  </Link>
                  <Link
                    to="/psykiatriske-lidelser/angst"
                    className="inline-flex items-center justify-center rounded-full border border-[#587b77] bg-white/50 py-2 px-3 text-xs sm:text-sm font-medium text-[#0f3834] transition hover:bg-[#dfe6dd]/50 text-center"
                  >
                    {lang === 'da' ? 'Angst' : 'Anxiety'}
                  </Link>
                  <Link
                    to="/psykiatriske-lidelser/ocd"
                    className="inline-flex items-center justify-center rounded-full border border-[#587b77] bg-white/50 py-2 px-3 text-xs sm:text-sm font-medium text-[#0f3834] transition hover:bg-[#dfe6dd]/50 text-center"
                  >
                    OCD
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* C.4 HVORNÅR BØR MAN OVERVEJE EN UDREDNING? (1 wide panel) */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#faf8f3]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="w-full bg-[#D1E1BE] rounded-none border-l-4 sm:border-l-[6px] border-l-[#9DB37E] px-8 sm:px-14 md:px-20 lg:px-24 py-12 sm:py-16 shadow-xs">
            <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-petrol-dark text-center mb-10">
              {cmsPage ? getLocalizedValue(cmsPage.overveje_overskrift) || (lang === 'da' ? 'Hvornår bør du overveje en ADHD-udredning?' : 'When should you consider an ADHD assessment?') : (lang === 'da' ? 'Hvornår bør du overveje en ADHD-udredning?' : 'When should you consider an ADHD assessment?')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 mx-auto">
              {considerPoints.map((pt, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-petrol-dark/10 text-petrol-dark flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 stroke-[2.5]" aria-hidden="true" />
                  </div>
                  <span className="text-xs sm:text-sm text-petrol-dark leading-relaxed">{pt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* C.5 UDREDNINGSFORLØBET (Process Stepper / Roadmap) */}
      <section className="bg-[#FAF8F5] py-20 px-6 font-sans border-t border-[#CCD7D2]/40">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center flex flex-col items-center">
            <span className="text-xs uppercase tracking-[0.25em] text-[#5E6F68] font-semibold">
              {cmsPage ? getLocalizedValue(cmsPage.proces_badge) || (lang === 'da' ? 'Struktureret Forløb' : 'Structured Pathway') : (lang === 'da' ? 'Struktureret Forløb' : 'Structured Pathway')}
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-[2.5rem] text-[#153830] font-normal leading-tight text-center">
              {cmsPage ? getLocalizedValue(cmsPage.proces_overskrift) || (lang === 'da' ? 'Sådan foregår en ADHD-udredning hos os' : 'How an ADHD assessment takes place with us') : (lang === 'da' ? 'Sådan foregår en ADHD-udredning hos os' : 'How an ADHD assessment takes place with us')}
            </h2>
            <div className="w-10 h-px bg-[#CCD7D2] mt-4 mb-16" />
          </div>

          {/* Timeline Grid */}
          <div className="relative">
            {/* Continuous Background Track Line (desktop) */}
            <div className="hidden md:block absolute top-[36px] left-[10%] right-[10%] h-px bg-[#CCD7D2] z-0" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4 relative z-10">
              {activeSteps.map((st, idx) => {
                const iconNode = stepIcons[idx % stepIcons.length];
                const circleBg = idx % 2 === 0 ? 'bg-[#E4ECE7]' : 'bg-[#F0EBE3]';
                return (
                  <div key={idx} className="flex flex-col items-center text-center">
                    <div className={`w-[72px] h-[72px] rounded-full ${circleBg} flex items-center justify-center text-[#153830] shrink-0 transition-transform duration-300 hover:scale-105`}>
                      {iconNode}
                    </div>
                    <h3 className="mt-6 font-display text-lg md:text-xl text-[#153830] font-normal leading-snug text-center max-w-[200px]">
                      {st.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#5E6F68] max-w-[210px] text-center font-sans">
                      {st.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LÆS MERE OM ADHD (Nordic Editorial Health 3-column card grid) */}
      <section className="bg-[#FCFDFC] py-16 md:py-20 px-6 font-sans border-t border-[#CCD7D2]/40">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <header className="mb-10 max-w-2xl">
            {/* Decorative Accent Rule */}
            <div className="w-8 h-[2px] bg-[#a3b8ad] rounded-full mb-4" />
            
            <h2 className="font-display text-3xl md:text-4xl text-[#173834] font-normal tracking-tight">
              {cmsPage && getLocalizedValue(cmsPage.artikler_overskrift) 
                ? getLocalizedValue(cmsPage.artikler_overskrift) 
                : (lang === 'da' ? 'Læs mere om ADHD' : 'Läs mer om ADHD')}
            </h2>
            <p className="mt-2 text-sm md:text-base text-[#61736F] leading-relaxed">
              {cmsPage && getLocalizedValue(cmsPage.artikler_undertitel)
                ? getLocalizedValue(cmsPage.artikler_undertitel)
                : (lang === 'da'
                    ? 'Vi deler viden og faglige artikler om ADHD, udredning, behandling og relaterede emner.'
                    : 'Vi delar kunskap och artiklar om ADHD, utredning, behandling och relaterade ämnen.')}
            </p>
          </header>

          {/* 3-Column Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {(cmsArticles.length > 0 ? cmsArticles.map(art => {
              const articleSlug = typeof art.slug === 'object' ? (art.slug[lang] || art.slug.da || Object.values(art.slug)[0]) : (art.slug || art.id);
              const title = getLocalizedValue(art.titel);
              const desc = getLocalizedValue(art.resume);
              return {
                slug: articleSlug,
                title,
                desc
              };
            }) : [
              {
                slug: 'ventetid-paa-adhd-udredning-offentligt-vs-privat',
                title: lang === 'da' ? 'Ventetid på ADHD-udredning – offentligt vs. privat' : 'Väntetid för ADHD-utredning – offentligt vs. privat',
                desc: lang === 'da' ? 'Sammenlign ventetid, pris og forløb i det offentlige og private tilbud.' : 'Jämför väntetid, pris och process mellan offentliga och privata alternativ.'
              },
              {
                slug: 'adhd-symptomer-hos-voksne-tegn-paa-adhd-og-add',
                title: lang === 'da' ? 'ADHD-symptomer hos voksne' : 'ADHD-symtom hos vuxna',
                desc: lang === 'da' ? 'Tegn på ADHD og ADD, og hvordan de kan komme til udtryk.' : 'Tecken på ADHD och ADD, och hur de kan yttra sig i vardagen.'
              },
              {
                slug: 'medicinsk-behandling-af-adhd-effekt-og-bivirkninger',
                title: lang === 'da' ? 'Medicinsk behandling af ADHD' : 'Medicinsk behandling av ADHD',
                desc: lang === 'da' ? 'Læs om effekt, bivirkninger og hvad du kan forvente.' : 'Läs om effekt, biverkningar och vad du kan förvänta dig.'
              },
              {
                slug: 'add-vs-adhd-hvad-er-forskellen',
                title: lang === 'da' ? 'ADD vs. ADHD – hvad er forskellen?' : 'ADD vs. ADHD – vad är skillnaden?',
                desc: lang === 'da' ? 'En gennemgang af de vigtigste ligheder og forskelle.' : 'En genomgång av de viktigaste likheterna och skillnaderna.'
              },
              {
                slug: 'audhd-naar-adhd-og-autisme-overlapper',
                title: lang === 'da' ? 'AuDHD – når ADHD og autisme overlapper' : 'AuDHD – när ADHD och autism överlappar',
                desc: lang === 'da' ? 'Om samspillet mellem ADHD og autisme, og hvordan det kan komme til udtryk.' : 'Om samspelet mellan ADHD och autism, och hur det kan yttra sig.'
              },
              {
                slug: 'diva-5-saadan-foregaar-en-adhd-udredning',
                title: lang === 'da' ? 'DIVA-5 – sådan foregår en ADHD-udredning' : 'DIVA-5 – så går en ADHD-utredning till',
                desc: lang === 'da' ? 'Få indsigt i, hvordan DIVA-5 bruges, og hvad du kan forvente i en udredning.' : 'Få insikt i hur DIVA-5 används och vad du kan förvänta dig i en utredning.'
              }
            ]).map((card, idx) => (
              <Link
                key={idx}
                to={`/vidensbank/${card.slug}`}
                className="group flex flex-col justify-between bg-white border border-[#E8ECE9] rounded-2xl p-6 sm:p-7 transition-all duration-200 hover:border-[#CBD5D0] hover:shadow-xs cursor-pointer"
              >
                <div>
                  <h3 className="font-display text-xl sm:text-2xl text-[#173834] font-normal leading-snug group-hover:text-[#2C4E49] transition-colors mb-3">
                    {card.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#61736F] leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* C.6 FAQ SECTION */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light">
        <FaqSchema 
          items={(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
            q: getLocalizedValue(f.spoergsmaal),
            a: getLocalizedValue(f.svar)
          })) : faqItems).slice(0, 7)} 
        />
        <div className="mx-auto max-w-[900px] px-6">
          <div className="bg-[#faf8f4] rounded-lg p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-petrol-dark mb-6">
              {cmsPage ? getLocalizedValue(cmsPage.faq_overskrift) || (lang === 'da' ? 'Ofte stillede spørgsmål om ADHD' : 'Vanliga frågor om ADHD') : (lang === 'da' ? 'Ofte stillede spørgsmål om ADHD' : 'Vanliga frågor om ADHD')}
            </h2>

            <div className="space-y-3">
              {(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
                q: getLocalizedValue(f.spoergsmaal),
                a: getLocalizedValue(f.svar)
              })) : faqItems).slice(0, 7).map((item, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div 
                    key={idx} 
                    className={`border-t border-[#d8d5ce] last:border-b border-[#d8d5ce] transition-all duration-200 ${
                      isOpen ? 'py-3' : 'py-1'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      aria-expanded={isOpen}
                      className={`flex w-full items-center justify-between text-left font-sans text-sm font-bold text-petrol-dark focus:outline-none transition-all duration-200 cursor-pointer ${
                        isOpen ? 'py-2' : 'py-1.5'
                      }`}
                    >
                      <span>{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-petrol-medium shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-petrol-medium shrink-0" />
                      )}
                    </button>
                    {/* Bevares altid i DOM for Google og crawlere */}
                    <div 
                      className={`text-xs sm:text-sm text-[#586067] leading-relaxed ${
                        isOpen ? 'mt-2 pb-2 block' : 'hidden'
                      }`}
                      dangerouslySetInnerHTML={typeof item.a === 'string' && item.a.includes('<') ? { __html: item.a } : undefined}
                    >
                      {typeof item.a === 'string' && item.a.includes('<') ? null : item.a}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection 
        title={ctaTitle}
        desc={ctaDesc}
        buttonText={ctaBtn}
      />
    </div>
  );
}
